import { c as _c } from "react/compiler-runtime";
import React from "react";
export interface PropertyDefinition {
  name: string;
  type: string;
  defaultValue?: string | number | boolean;
  value?: string | number | boolean;
  description?: string;
}
interface FixturePropsTableProps {
  properties: PropertyDefinition[];
  title?: string;
  variant?: "full" | "compact";
}

/**
 * A functional component for rendering a table that displays properties of a design system.
 *
 * @param {Object} props - The props for the component.
 * @param {Array<Object>} props.properties - An array of objects representing design system properties. Each object must include `name`, `type`, and may optionally include `defaultValue`, `value`, and `description`.
 * @param {string} [props.title] - An optional title for the table.
 * @param {string} [props.variant="full"] - The display variant of the table. Defaults to "full". Determines whether additional columns, such as descriptions, are shown.
 *
 * @returns {JSX.Element} A table displaying the provided properties, their types, default values, values, and optionally their descriptions.
 */
export const FixturePropsTable = t0 => {
  const $ = _c(40);
  const {
    properties,
    title,
    variant: t1
  } = t0;
  const variant = t1 === undefined ? "full" : t1;
  let t2;
  if ($[0] !== properties) {
    t2 = properties.some(_temp);
    $[0] = properties;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const hasDefaultValues = t2;
  let t3;
  if ($[2] !== properties) {
    t3 = properties.some(_temp2);
    $[2] = properties;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const hasValues = t3;
  let t4;
  if ($[4] !== properties) {
    t4 = properties.some(_temp3);
    $[4] = properties;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const hasDescriptions = t4;
  const formatValue = _temp4;
  const t5 = title ?? "\u0412\u0445\u043E\u0434\u044F\u0449\u0438\u0435 \u0441\u0432\u043E\u0439\u0441\u0442\u0432\u0430 \u043A\u043E\u043C\u043F\u043E\u043D\u0435\u043D\u0442\u0430";
  let t6;
  if ($[6] !== t5) {
    t6 = <h3 className="text-base font-semibold tracking-tight">{t5}</h3>;
    $[6] = t5;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  let t7;
  let t8;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <th className="px-4 py-3" scope="col">Свойство</th>;
    t8 = <th className="px-4 py-3" scope="col">Тип</th>;
    $[8] = t7;
    $[9] = t8;
  } else {
    t7 = $[8];
    t8 = $[9];
  }
  let t9;
  if ($[10] !== hasDefaultValues) {
    t9 = hasDefaultValues && <th className="px-4 py-3" scope="col">По умолчанию</th>;
    $[10] = hasDefaultValues;
    $[11] = t9;
  } else {
    t9 = $[11];
  }
  let t10;
  if ($[12] !== hasValues) {
    t10 = hasValues && <th className="px-4 py-3" scope="col">Значение</th>;
    $[12] = hasValues;
    $[13] = t10;
  } else {
    t10 = $[13];
  }
  let t11;
  if ($[14] !== hasDescriptions || $[15] !== variant) {
    t11 = hasDescriptions && variant === "full" && <th className="px-4 py-3" scope="col">Описание</th>;
    $[14] = hasDescriptions;
    $[15] = variant;
    $[16] = t11;
  } else {
    t11 = $[16];
  }
  let t12;
  if ($[17] !== t10 || $[18] !== t11 || $[19] !== t9) {
    t12 = <thead className="border-b bg-muted/50 text-xs font-medium tracking-wide uppercase"><tr className="font-semibold">{t7}{t8}{t9}{t10}{t11}</tr></thead>;
    $[17] = t10;
    $[18] = t11;
    $[19] = t9;
    $[20] = t12;
  } else {
    t12 = $[20];
  }
  let t13;
  if ($[21] !== hasDefaultValues || $[22] !== hasDescriptions || $[23] !== hasValues || $[24] !== properties || $[25] !== variant) {
    let t14;
    if ($[27] !== hasDefaultValues || $[28] !== hasDescriptions || $[29] !== hasValues || $[30] !== variant) {
      t14 = (prop_2, index) => <tr key={`${prop_2.name}-${index}`} className="transition-colors hover:bg-muted/20"><td className="px-4 py-3 font-mono text-xs md:text-sm">{prop_2.name}</td><td className="px-4 py-3 font-mono text-xs md:text-sm">{prop_2.type}</td>{hasDefaultValues && <td className="px-4 py-3 font-mono text-xs md:text-sm">{formatValue(prop_2.defaultValue)}</td>}{hasValues && <td className="px-4 py-3 font-mono text-xs md:text-sm">{formatValue(prop_2.value)}</td>}{hasDescriptions && variant === "full" && <td className="px-4 py-3 text-sm text-muted-foreground">{prop_2.description || "\u2014"}</td>}</tr>;
      $[27] = hasDefaultValues;
      $[28] = hasDescriptions;
      $[29] = hasValues;
      $[30] = variant;
      $[31] = t14;
    } else {
      t14 = $[31];
    }
    t13 = properties.map(t14);
    $[21] = hasDefaultValues;
    $[22] = hasDescriptions;
    $[23] = hasValues;
    $[24] = properties;
    $[25] = variant;
    $[26] = t13;
  } else {
    t13 = $[26];
  }
  let t14;
  if ($[32] !== t13) {
    t14 = <tbody className="divide-y">{t13}</tbody>;
    $[32] = t13;
    $[33] = t14;
  } else {
    t14 = $[33];
  }
  let t15;
  if ($[34] !== t12 || $[35] !== t14) {
    t15 = <div className="overflow-x-auto rounded-sm border"><table className="w-fit max-w-full text-left text-sm md:text-base">{t12}{t14}</table></div>;
    $[34] = t12;
    $[35] = t14;
    $[36] = t15;
  } else {
    t15 = $[36];
  }
  let t16;
  if ($[37] !== t15 || $[38] !== t6) {
    t16 = <div className="flex w-full flex-col gap-4">{t6}{t15}</div>;
    $[37] = t15;
    $[38] = t6;
    $[39] = t16;
  } else {
    t16 = $[39];
  }
  return t16;
};
function _temp(prop) {
  return prop.defaultValue !== undefined;
}
function _temp2(prop_0) {
  return prop_0.value !== undefined;
}
function _temp3(prop_1) {
  return prop_1.description !== undefined;
}
function _temp4(value) {
  if (value === undefined) {
    return "\u2014";
  }
  if (typeof value === "boolean") {
    return value ? "true" : "false";
  }
  return String(value);
}